import { CombinedGraphQLErrors } from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toErrorArray = (
  value: unknown,
): readonly GraphQLFormattedError[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  return value as readonly GraphQLFormattedError[];
};

export const toCombinedGraphQLErrors = (
  input: unknown,
): CombinedGraphQLErrors | null => {
  if (CombinedGraphQLErrors.is(input)) {
    return input;
  }

  if (!isObject(input)) {
    return null;
  }

  const operationError = input.error;
  if (CombinedGraphQLErrors.is(operationError)) {
    return operationError;
  }

  const legacyErrors = toErrorArray(input.errors);
  if (!legacyErrors) {
    return null;
  }

  return new CombinedGraphQLErrors({
    errors: legacyErrors,
  });
};

export const getGraphQLErrors = (
  input: unknown,
): readonly GraphQLFormattedError[] => {
  const combined = toCombinedGraphQLErrors(input);
  return combined?.errors ?? [];
};

export const hasGraphQLErrorCode = (input: unknown, code: string): boolean =>
  getGraphQLErrors(input).some((err) => err?.extensions?.code === code);

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  return typeof value === 'string' ? [value] : [];
};

const getValidationMessagesFromError = (
  error: GraphQLFormattedError,
): string[] => {
  const validationErrors = error.extensions?.validationErrors;
  if (!isObject(validationErrors)) {
    return [];
  }

  return Object.values(validationErrors).flatMap(toStringArray);
};

export const getValidationErrorMessages = (input: unknown): string[] =>
  getGraphQLErrors(input).flatMap(getValidationMessagesFromError);
